import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpClient } from '@angular/common/http';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { FileUploadModule } from 'primeng/fileupload';
import { UserService } from '../../../../../../../shared/services/user.service';
import { ToastService } from '../../../../../../../shared/services/toast.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { UserClientVm } from '../../../../../../../store/user/user.interface';
import { constants } from '../../../../../../../shared/constants/constants';
import { environment } from '../../../../../../../../environments/environment';
import { InputTextareaModule } from 'primeng/inputtextarea';

@Component({
    selector: 'app-manual-approval',
    imports: [TableModule, FileUploadModule, ButtonModule, ConfirmDialogModule, CommonModule, TooltipModule, InputTextModule, FormsModule, InputTextareaModule, ReactiveFormsModule],
    templateUrl: './manual-approval-creds-education.component.html',
    styleUrl: './manual-approval-creds-education.component.scss',
    standalone: true,
    providers: [ConfirmationService, MessageService]
})
export class ManualCredentialEducationApprovalComponent implements OnInit {
  approvalForm!: FormGroup;
  user: UserClientVm;
  selectedDocument: File | null = null;
  reviewerId = '12345'; // Example ID, replace dynamically
  selectedCredential:any = null;
  selectedCredentials:any[] = [];
  educationCredentials: any[] = [];
  selectedStatusSummary = { type: 'AUDIT_REVIEW', _id: '67890' };
  credsOnly = false;
  constants = constants;
  constructor(
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private http: HttpClient,
    private config: DynamicDialogConfig,
    private userService: UserService,
    private toast: ToastService,
    private ref: DynamicDialogRef
  ) {
    
    this.user = this.userService.getSessionUser();
    this.approvalForm = this.fb.group({
      approvedByName: [{ value: this.user.email, disabled: true }, Validators.required],
      auditTrialInformation: ['']
    });
  }

  ngOnInit(): void {
    this.reviewerId = this.config.data.reviewer._id;
    this.credsOnly = this.config.data.credsOnly || false;
    this.selectedCredentials = this.config.data.credentials || [];
    this.educationCredentials = this.config.data.educationCredentials || [];

  }

  onFileSelect(event: any): void {
    this.selectedDocument = event.files[0];
  }

  closeDialog() {
    this.ref.close();
  }
  confirmApproval(refuse: boolean): void {
    if (this.approvalForm.invalid) return;

    const action = refuse ? 'refuse' : 'approve';
    let credentialsDataLabels = '<br>';

    const credsCount = this.selectedCredentials.length;
    const educationCount = this.educationCredentials.length;

    const types = [
      educationCount > 0 && 'Education',
      credsCount > 0 && 'Credentials'
    ].filter(Boolean);
    const typeLabel = types.join(' and ');

    this.educationCredentials.forEach((credential: any) => {
      credentialsDataLabels += `${credential.school} - ${credential.description} <br>`;
    });
    this.selectedCredentials.forEach((credential: any) => {
      credentialsDataLabels += `${credential.school} - ${credential.description} <br>`;
    });
    
    this.confirmationService.confirm({
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: refuse ? 'Deny' : 'Approve',
      rejectLabel: 'Cancel',
      message: `You are about to ${action} the following ${typeLabel} for ${this.config?.data.reviewer.firstName} ${this.config?.data.reviewer.lastName}? <br>
      ${credentialsDataLabels}`,
      accept: () => this.submitApproval(refuse)
    });
  }

  /**
   * Submits the approval or rejection of selected credentials and education records
   * @param refuse - Boolean flag indicating whether to refuse (true) or approve (false) the items
   */
  async submitApproval(refuse: boolean): Promise<void> {
    const action = refuse ? 'refuse' : 'approve';
    
    try {
      const uploadResponse = await this.handleDocumentUpload();
      const requestObject = this.buildRequestObject(uploadResponse);

      if (refuse) {
        await this.handleRejection(requestObject, action);
      } else {
        await this.handleApproval(requestObject, action);
      }
    } catch (error) {
      this.handleError(error, action);
    }
  }

  private async handleDocumentUpload(): Promise<any> {
    if (!this.shouldUploadDocument()) {
      return { documentIds: null };
    }

    const formData = this.createUploadFormData();
    const response: any = await this.http.post(environment.apiUrl + 'documents', formData).toPromise();
    return response;
  }

  private shouldUploadDocument(): boolean {
    return !!(this.selectedDocument && (this.selectedCredentials.length > 0 || this.educationCredentials.length > 0));
  }

  private createUploadFormData(): FormData {
    const formData = new FormData();
    formData.append('fileUpload', this.selectedDocument!);
    formData.append('documentType', 'AUDIT_TRIAL');
    formData.append('reviewerId', this.reviewerId);
    formData.append('isClientEmailUpload', 'true');
    return formData;
  }

  private buildRequestObject(uploadResponse: any): any {
    return {
      type: constants.bgCheckTypes.CREDENTIALS,
      typeIds: this.selectedCredentials.map((creds: any) => creds._id),
      firmId: this.user.firm._id ? this.user.firm._id : this.user.firm,
      approvedByName: this.approvalForm.controls['approvedByName'].value,
      auditTrialInformation: this.approvalForm.controls['auditTrialInformation'].value,
      auditDocumentId: uploadResponse.documentIds,
      verifyCredentialOnly: true
    };
  }

  private async handleRejection(requestObject: any, action: string): Promise<void> {
    await this.processCredentialsRejection(requestObject);
    await this.processEducationRejection(requestObject);
    this.showSuccessAndClose(action);
  }

  private async processCredentialsRejection(requestObject: any): Promise<void> {
    if (requestObject.typeIds && requestObject.typeIds.length) {
      await this.userService.manualRejectCredentialBulk(this.reviewerId, requestObject);
    }
  }

  private async processEducationRejection(requestObject: any): Promise<void> {
    requestObject.type = constants.bgCheckTypes.EDUCATION;
    requestObject.typeIds = this.educationCredentials.map((creds: any) => creds._id);
    
    if (requestObject.typeIds && requestObject.typeIds.length) {
      await this.userService.manualRejectEducationBulk(this.reviewerId, requestObject);
    }
  }

  private async handleApproval(requestObject: any, action: string): Promise<void> {
    if(this.educationCredentials.length){
      await this.processEducationApproval(requestObject);
    }
    if(this.selectedCredentials.length){
      await this.processCredentialsApproval(requestObject);
    }
    
    if (this.credsOnly) {
      this.showSuccessAndClose(action);
    } else {
      this.approveUserAndClose(action);
    }
  }

  private async processCredentialsApproval(requestObject: any): Promise<void> {
    requestObject.type = constants.bgCheckTypes.CREDENTIALS;
    requestObject.typeIds = this.selectedCredentials.map((creds: any) => creds._id);
    if (requestObject.typeIds && requestObject.typeIds.length) {
      await this.userService.manualApproveCredentialBulk(this.reviewerId, requestObject);
    }
  }

  private async processEducationApproval(requestObject: any): Promise<void> {
    requestObject.type = constants.bgCheckTypes.EDUCATION;
    requestObject.typeIds = this.educationCredentials.map((creds: any) => creds._id);
    if(!this.selectedCredentials.length){
      requestObject.verifyCredentialOnly = false;
    }
    
    if (requestObject.typeIds && requestObject.typeIds.length) {
      await this.userService.manualApproveEducationBulk(this.reviewerId, requestObject);
    }
  }

  private approveUserAndClose(action: string): void {
    this.userService.approveUser(this.reviewerId).subscribe((response: any) => {
      this.showSuccessAndClose(action);
    });
  }

  private showSuccessAndClose(action: string): void {
    this.closeDialog();
    const itemCount = this.selectedCredentials.length + this.educationCredentials.length;
    const itemType = 'items';
    this.toast.showSuccess(`${itemCount} ${itemType} ${action}d successfully`);
  }

  private handleError(error: any, action: string): void {
    this.closeDialog();
    this.toast.showError(`${action} failed`);
  }
}
